import type { CreateListingForm } from '@c/CreateListingPage';
import { BrandProp } from 'pages/brands';
import { z } from 'zod';
import { datetimeLocalToTimestamp, inBetween, timestampFromDuration } from '.';
import { redactPhonesAndEmailsFromStr } from './firestore/messages';
import { CONDITIONS, CONDITION_LABEL_MAP } from './maps/conditions';
import {
  ProductDocument,
  productDocumentSchema,
} from './types/firestore/products';
import { ConditionDocument } from './types/firestore/conditions';
import { BrandDocument } from './types/firestore/brand';
import { slugify } from './urlHelpers';
import { logError } from './logError';

type ListingImage = CreateListingForm['productDetail']['productImages'][number];

/** The form uses these callbacks directly; tests supply inert image services. */
export function createListingImageHandlers({
  images,
  setImages,
  revokeObjectURL,
  deleteListingImage,
  onDeleteError,
  triggerValidation,
}: {
  // Runtime form data can contain holes even though the schema forbids them.
  images: ListingImage[] | null | undefined;
  setImages: (images: ListingImage[], options?: { shouldValidate: true }) => void;
  revokeObjectURL: (url: string) => void;
  deleteListingImage: (remotePath: string) => unknown;
  onDeleteError: (error: unknown) => void;
  triggerValidation: () => void;
}) {
  async function deleteImage(img: ListingImage | null | undefined) {
    if (img == null) return;
    const newImages = (images ?? []).filter(
      (image): image is ListingImage =>
        image != null && image.localUrl !== img.localUrl
    );
    revokeObjectURL(img.localUrl);
    try {
      deleteListingImage(img.remotePath);
    } catch (error) {
      onDeleteError(error);
    }
    setImages(newImages);
    triggerValidation();
  }

  function handleImageDrop(
    event: { dataTransfer: Pick<DataTransfer, 'getData'> },
    dropIndex: number
  ) {
    const rawDragIndex = event.dataTransfer.getData('text/plain');
    const dragIndex = Number(rawDragIndex);
    // Only the canonical nonnegative index written by onDragStart is valid.
    // Numeric conversion alone accepts fractions and alternate representations.
    if (
      !Number.isInteger(dragIndex) ||
      dragIndex < 0 ||
      String(dragIndex) !== rawDragIndex
    ) return;
    const newImages = [...(images ?? [])];
    const [draggedImage] = newImages.splice(dragIndex, 1);
    // A missing source (hole or stale/out-of-range index) must not be inserted.
    if (draggedImage == null) return;
    newImages.splice(dropIndex, 0, draggedImage);
    setImages(newImages, { shouldValidate: true });
  }

  return { deleteImage, handleImageDrop };
}

export function getConditionOptions() {
  return CONDITIONS.map((c) => ({
    label: CONDITION_LABEL_MAP[c],
    value: CONDITION_LABEL_MAP[c],
    id: c,
  }));
}

export function getBrandOptions(brands: BrandDocument[]) {
  if (!brands?.length) return [];
  
  const brandsObj = brands.map((brand) => ({
    label: brand.name,
    value: brand.name,
    id: brand.name,
  }))
  
  return brandsObj;
}

export function getItemConditionOptions(conditions: ConditionDocument[]) {
  if (!conditions?.length) return [];
  return conditions.map((condition) => ({
    label: condition.name,
    value: condition.name,
  }));
}

export function getCurrencyOptions() {
  const CURRENCIES = ['USD', 'CAD'];
  return CURRENCIES.map((currency) => ({
    label: currency,
    value: currency,
  }));
}

/**
 * The number that belongs in the edit form's "Price" box for an existing
 * listing.
 *
 * The invariant the save path below enforces is:
 *   sale OFF -> price === og_price  (og_price is just a copy of the price)
 *   sale ON  -> og_price = the list price, price = the discounted price
 *
 * Partner integrations own the `price` field on the listings they sync and
 * mark it down over time (gearfocus-api writes `price` only, never
 * `og_price`). On those listings og_price is therefore NOT a list price at
 * all — it is a relic of whatever the item cost when it was first imported,
 * frozen months ago. Seeding the price box from og_price meant that ANY save
 * through this form wrote that relic back as the live price, including a save
 * that never touched the price box at all (edit the shipping cost, hit Save,
 * the price silently jumps back to the old number).
 *
 * So: `price` is the authority EXCEPT in the two states where og_price
 * carries its own distinct meaning and overwriting it would destroy real
 * information:
 *
 *   on_sale   -> og_price is the struck-through "was" price the buyer sees
 *   is_auction -> og_price is the STARTING BID. Verified: AuctionStatus.tsx
 *                 renders `highestBid.bid_price` once bids exist and falls
 *                 back to `og_price` labelled "Starting Bid" when there are
 *                 none (:150-176). 95 live auctions have price != og_price,
 *                 so seeding the box from `price` would overwrite the
 *                 starting bid on the next save — and ended auctions ARE
 *                 reachable in this form, because ListingsPage.tsx gates Edit
 *                 on `|| product.is_auction` and so bypasses the out_of_stock
 *                 check (see the deleted_at note further down). What sets
 *                 `price` above og_price on an ended auction is NOT
 *                 established from this repo — the bid path writes to the
 *                 `bids` collection, not to product.price. The carve-out is
 *                 here to hold parity with pre-fix behaviour on a field whose
 *                 write path we have not traced, which is reason enough.
 *
 * Both flags are compared against `true` rather than tested for truthiness.
 * 1,758 documents in `products` have no `on_sale` at all — but they are not
 * legacy listings, they are orphan field-fragments written by the gearMaster
 * linker (verified: 0 of the 1,758 have a title, price, og_price, or
 * seller_id; a typical one holds only {gearMaster, has_gearMaster, images,
 * thumbnail}). They cannot reach this form. The `=== true` form is still the
 * right comparison, just not for the reason a "legacy listings" reading would
 * suggest.
 *
 * GB #1665. Investigated 2026-08-06: 707 in-stock listings were sitting on a
 * stale og_price higher than their live price at the time of the fix. None of
 * those 707 are auctions, so the auction carve-out costs no coverage.
 */
export function getEditFormListPrice(product: ProductDocument): number {
  if (product.on_sale === true) return product.og_price;
  if (product.is_auction === true) return product.og_price;
  // `price` is only trusted when it is a usable money value. Before the fix
  // this line read og_price unconditionally, which is always numeric, so a
  // stored price of 0/NaN/a string could never reach the form; guarding keeps
  // it that way rather than letting the fix open a new hole. Without it a 0
  // would publish a free listing and a string would throw a raw Zod error at
  // the seller on save.
  //
  // This is NOT hypothetical: 94 draft listings currently have an unusable
  // price and are reachable in this very form, because ListingsPage.tsx:260
  // opens Edit on `product.is_draft || !product.out_of_stock ||
  // product.is_auction`. Measured, every one of the 94 holds exactly `0`
  // (no nulls, no NaN, no strings), so they take this fallback and behave
  // exactly as they did pre-fix. (An earlier version of this comment claimed
  // no listing was in that state at all; that was an artefact of a scan that
  // filtered drafts out by construction.)
  //
  // Known limit, deliberately not fixed here: a `price` of null/undefined
  // would throw one line further down in buildPriceAndInventoryDefaults, at
  // `product!.price.toString()` for the sale box, before this fallback is
  // observable. That throw is pre-existing and identical on main, and no
  // document is currently in that shape — so closing it is a separate,
  // sized change rather than something to smuggle in here.
  const price = product.price;
  if (typeof price === 'number' && Number.isFinite(price) && price > 0) {
    return price;
  }
  return product.og_price;
}

/**
 * Default values for the edit/create form's "Price & inventory" section.
 *
 * This lives here rather than inline in CreateListingPage's `defaultValues`
 * so that the price-box rule above is reachable from a test. When it was
 * inline, the whole of #1665 could be reverted in the component and every
 * test still passed, because the tests called getEditFormListPrice directly
 * and so re-implemented the component's wiring instead of exercising it.
 *
 * Be precise about what that buys, because it is easy to overstate: reverting
 * the rule INSIDE this function now fails 6 tests. Re-inlining the whole
 * defaults object back into the component would still pass, because no test
 * renders CreateListingPage. What this extraction actually guarantees is that
 * the component holds no price logic left to drift — not that a determined
 * re-inline would be caught. Keep this function as the ONLY place the form's
 * price defaults are built.
 */
export function buildPriceAndInventoryDefaults(
  // Null as well as undefined: the create path passes an explicitly null
  // product, and the previous inline code reached both through `product?.`.
  product: ProductDocument | null | undefined,
  variant: 'edit' | 'create'
) {
  return {
    quantityInStock: product?.stock ?? 1,
    currency: 'USD',
    price: product ? getEditFormListPrice(product).toString() : '',
    saleEnabled: variant === 'edit' ? product!.on_sale : false,
    productSale: variant === 'edit' ? product!.price.toString() : '',
  };
}

type buildProductDocArgs = {
  isDraft: boolean;
  form: CreateListingForm;
  userId: string;
  productCountry: string;
  productId: string;
  accountId: string;
  isAdmin?: boolean;
  currProduct?: ProductDocument | null;
  auctionStartTime?: string;
};

export function buildProductDocumentFromCreateListingForm({
  form,
  isDraft,
  userId,
  productCountry,
  productId,
  accountId,
  isAdmin,
  currProduct,
  auctionStartTime,
}: buildProductDocArgs): ProductDocument {
  const timestamp = Date.now();

  const {
    productDetail,
    priceAndInventory,
    makeAnOffer,
    shipping,
    auction,
    ebay,
  } = form;
  const {
    title,
    gearMaster,
    brand,
    condition,
    sku,
    mainCategory,
    secondaryCategory,
    productDescription,
    productImages,
  } = productDetail;
  const {
    price: initialPrice,
    productSale,
    quantityInStock,
    saleEnabled,
  } = priceAndInventory;

  const { minimumOffer, autoDecline, enabled: offersEnabled } = makeAnOffer;
  const { gearfocusShippingLabel, regionalShippingCosts, isFlatRate } =
    shipping;

  const og_price = z.number().parse(getNumber(initialPrice));
  const discount = saleEnabled
    ? og_price - z.number().parse(getNumber(productSale))
    : 0;
  const price = og_price - discount;

  // eBay price override (optional). Stored only when "Also list on eBay" is
  // on AND the seller typed a different number; otherwise null so an
  // update on edit explicitly clears any previously stored value (Firestore
  // updateDoc with `null` removes the seller's stale preference).
  let ebay_price: number | null = null;
  if (ebay?.enabled && ebay.price && ebay.price.trim() !== '') {
    const parsedEbay = getNumber(ebay.price);
    if (Number.isFinite(parsedEbay) && parsedEbay > 0) {
      ebay_price = parsedEbay;
    }
  }
  // Publish mode. Null on edit if eBay is off so the existing field is
  // explicitly cleared (same reasoning as ebay_price).
  const ebay_publish_mode: 'draft' | 'live' | null = ebay?.enabled
    ? ebay.publish_mode === 'live'
      ? 'live'
      : 'draft'
    : null;
  const redactedDescription = redactPhonesAndEmailsFromStr(productDescription);

  const categories = [];
  if (mainCategory) categories.push(mainCategory);
  if (secondaryCategory) categories.push(secondaryCategory);

  // Draft saves can bypass form validation. Keep images and thumbnail based
  // on the same complete entries even if runtime form data contains holes.
  const completeImages = (productImages ?? []).filter(
    (image) =>
      image != null &&
      typeof image.thumb === 'string' &&
      typeof image.full === 'string'
  );
  if (completeImages.length < (productImages ?? []).length) {
    void logError(
      new Error(
        `Incomplete listing images dropped: droppedCount=${(productImages ?? []).length - completeImages.length}, totalCount=${(productImages ?? []).length}`
      )
    );
  }

  const productDocument: ProductDocument = {
    brand,
    bumps: currProduct?.bumps ?? 0,
    categories,
    // gearMaster array: passed directly from form (already an array)
    gearMaster: gearMaster ?? [],
    condition,
    country_code: productCountry,
    created: currProduct?.created ?? timestamp,
    date_added: currProduct?.is_expired ? timestamp : (currProduct?.date_added ?? timestamp),
    date_updated: timestamp,
    description_html: redactedDescription,
    favorite_count: currProduct?.favorite_count ?? 0,
    from_web: true,
    id: productId,
    images: completeImages.map((image) => ({
      thumb: image.thumb,
      full: image.full,
    })),
    is_draft: isDraft,
    is_verified: currProduct?.is_draft ? false : (currProduct?.is_verified ?? false),
    is_expired: false,
    // api#295: this is the actual "renew a deleted listing" path. The
    // dashboard's Edit Item / product page's Edit-or-Renew button reaches
    // this form even for a deleted (out_of_stock) listing when it's an
    // auction (ListingsPage.tsx gates Edit on `|| product.is_auction`,
    // bypassing the out_of_stock check) or once is_expired flips back to
    // false. This function already resets is_expired unconditionally on
    // every save (see above), so deleted_at follows the same rule: any
    // explicit save through this form clears the tombstone, matching how
    // out_of_stock itself gets freshly recomputed below instead of carried
    // over from currProduct.
    deleted_at: null,
    is_featured: currProduct?.is_featured ?? false,
    is_fraudulent: currProduct?.is_fraudulent ?? false,
    minimum_offer_price: autoDecline
      ? z.number().parse(getNumber(minimumOffer))
      : 0,
    offers_enabled: offersEnabled ?? true,
    is_auction: !!auction?.enabled,
    is_auction_live:
      !!auction?.enabled && auctionStartTime
        ? inBetween(auctionStartTime, auction?.duration)
        : false,
    start_time:
      auction?.enabled && auctionStartTime
        ? datetimeLocalToTimestamp(auctionStartTime)
        : 0,
    end_time: currProduct?.end_time
      ? currProduct.end_time
      : auction?.enabled && auctionStartTime
      ? timestampFromDuration(Number(auction.duration), auctionStartTime)
      : 0,
    og_price,
    // Always include both fields: a real value when eBay is on, explicit
    // null when off so updateDoc clears any previously stored preference.
    ebay_price,
    ebay_publish_mode,
    old_id: currProduct?.old_id ?? 0,
    old_seller_id: currProduct?.old_seller_id ?? 0,
    on_sale: saleEnabled,
    out_of_stock:
      currProduct?.on_vacation === true ||
      quantityInStock === 0 ||
      isDraft ||
      !!(
        auction?.enabled &&
        auctionStartTime &&
        !inBetween(auctionStartTime, auction?.duration)
      ),
    price,
    seller_id: isAdmin ? currProduct?.seller_id ?? userId : userId,
    account_id: accountId ?? '',
    sku,
    slug: slugify(title, productId),
    stock: auction?.enabled ? 1 : quantityInStock,
    source: 'web',
    taxable: true,
    thumbnail: completeImages[0]?.thumb ?? '',
    title,
    is_flat_rate: isFlatRate,
    rank_score: 5
  };

  if (
    !isFlatRate &&
    gearfocusShippingLabel &&
    gearfocusShippingLabel.ship_from
  ) {
    // Shipengine model
    const pounds = gearfocusShippingLabel.package.weight?.pounds ?? 0;
    const ounces =
      pounds * 16 + (gearfocusShippingLabel.package.weight?.ounces ?? 0);
    productDocument.shipment = {
      ship_from: gearfocusShippingLabel.ship_from,
      package: {
        weight: {
          unit: 'ounce',
          value: ounces,
        },
        dimensions: {
          unit: 'inch',
          ...gearfocusShippingLabel.package.dimensions,
        },
        ...(gearfocusShippingLabel.shippingInsurance && {
          insured_value: {
            currency: 'usd',
            amount: productDocument.price,
          },
        }),
        // Always write this field so toggling insurance ON clears a stale opt-out
        insurance_opted_out: !gearfocusShippingLabel.shippingInsurance,
      },
    };
    delete productDocument.shipping_costs;
  } else if (regionalShippingCosts) {
    productDocument.shipping_costs = regionalShippingCosts.map((obj) => ({
      cost: z.number().parse(getNumber(obj.cost)),
      code: obj.code ?? '',
      name: obj.name ?? '',
    }));
    delete productDocument.shipment;
  } else {
    throw new Error('No shipping costs or shipment found');
  }

  return productDocumentSchema.parse(productDocument);
}

export const getNumber = (numStr?: string) =>
  numStr ? Number(numStr.replace(/,/g, '')) : 0;
